import { Box } from "@mantine/core"
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { useCallback, useMemo, useState } from "react"
import type {
  DocumentCategory,
  FlatItem,
  TreeNode,
} from "../models/documentCategory"
import { useReorderDocumentCategories } from "../hooks/useDocumentCategories"
import TreeItem from "./TreeItem"

function buildFlatList(
  items: DocumentCategory[],
  expanded: Set<number>,
): FlatItem[] {
  const itemMap = new Map(items.map((i) => [i.id, i]))
  const childrenOf = new Map<number, number[]>()
  const roots: number[] = []

  for (const item of items) {
    if (item.root === 0) {
      roots.push(item.id)
    } else {
      const siblings = childrenOf.get(item.root) ?? []
      siblings.push(item.id)
      childrenOf.set(item.root, siblings)
    }
  }

  const sortByOrder = (ids: number[]) =>
    ids.sort((a, b) => {
      const ia = itemMap.get(a)
      const ib = itemMap.get(b)
      return (ia?.order ?? 0) - (ib?.order ?? 0)
    })
  sortByOrder(roots)
  for (const [, siblings] of childrenOf) sortByOrder(siblings)

  const result: FlatItem[] = []

  function walk(ids: number[], depth: number) {
    for (const id of ids) {
      const item = itemMap.get(id)
      if (!item) continue
      const children = childrenOf.get(id) ?? []
      result.push({
        id: item.id,
        name: item.name,
        root: item.root,
        order: item.order,
        hierarchy_name: item.hierarchy_name,
        depth,
        hasChildren: children.length > 0,
      })
      if (expanded.has(id)) walk(children, depth + 1)
    }
  }

  walk(roots, 0)
  return result
}

function treeNodeFromItems(items: DocumentCategory[]): TreeNode[] {
  const rootNodes: TreeNode[] = []
  const sorted = [...items].sort((a, b) => {
    if (a.root !== b.root) return a.root - b.root
    return a.order - b.order
  })
  const created = new Map<number, TreeNode>()
  for (const item of sorted) {
    const node: TreeNode = { ...item, depth: 0, children: [] }
    created.set(node.id, node)
    if (node.root === 0) {
      rootNodes.push(node)
    } else {
      const parent = created.get(node.root)
      if (parent) {
        node.depth = parent.depth + 1
        parent.children.push(node)
      } else {
        rootNodes.push(node)
      }
    }
  }
  return rootNodes
}

interface Props {
  items: DocumentCategory[]
  selectedId: number | null
  onSelect: (node: TreeNode) => void
  onEdit: (node: TreeNode) => void
  onAddChild: (node: TreeNode) => void
}

export default function CategoryTreeView({
  items,
  selectedId,
  onSelect,
  onEdit,
  onAddChild,
}: Props) {
  const reorderMutation = useReorderDocumentCategories()

  const [expanded, setExpanded] = useState<Set<number>>(() => {
    const parents = new Set<number>()
    for (const item of items) {
      if (items.some((c) => c.root === item.id)) {
        parents.add(item.id)
      }
    }
    return parents
  })

  const flatItems = useMemo(
    () => buildFlatList(items, expanded),
    [items, expanded],
  )
  const visibleIds = useMemo(() => flatItems.map((i) => i.id), [flatItems])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const toggleExpand = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const activeId = active.id as number
      const overId = over.id as number

      const dragItem = items.find((i) => i.id === activeId)
      const overItem = items.find((i) => i.id === overId)
      if (!dragItem || !overItem) return

      const oldRoot = dragItem.root
      const activeParentId = active.data.current?.parentId ?? 0
      const overParentId = over.data.current?.parentId ?? 0

      const newRoot =
        activeParentId !== overParentId ? overItem.id : oldRoot

      const updated = items.map((i) => {
        if (i.id === activeId) return { ...i, root: newRoot, order: i.order }
        return { ...i }
      })

      const byRoot = new Map<number, DocumentCategory[]>()
      for (const item of updated) {
        const list = byRoot.get(item.root) ?? []
        list.push(item)
        byRoot.set(item.root, list)
      }
      for (const [, list] of byRoot) list.sort((a, b) => a.order - b.order)

      if (oldRoot !== newRoot) {
        const oldList = byRoot.get(oldRoot) ?? []
        oldList.forEach((i, idx) => { i.order = idx })
      }

      const newList = byRoot.get(newRoot) ?? []
      const draggedIdx = newList.findIndex((i) => i.id === activeId)
      const targetIdx = newList.findIndex((i) => i.id === overId)
      if (draggedIdx !== -1 && targetIdx !== -1 && draggedIdx !== targetIdx) {
        const [moved] = newList.splice(draggedIdx, 1)
        newList.splice(targetIdx, 0, moved)
      }
      newList.forEach((i, idx) => { i.order = idx })

      reorderMutation.mutate(
        updated.map((i) => ({ id: i.id, root: i.root, order: i.order })),
      )
    },
    [items, reorderMutation],
  )

  const findTreeNode = useCallback(
    (id: number): TreeNode | undefined => {
      const tree = treeNodeFromItems(items)
      const walk = (nodes: TreeNode[]): TreeNode | undefined => {
        for (const n of nodes) {
          if (n.id === id) return n
          const f = walk(n.children)
          if (f) return f
        }
      }
      for (const root of tree) {
        const f = walk([root])
        if (f) return f
      }
    },
    [items],
  )

  const handleSelect = useCallback(
    (node: FlatItem) => {
      const t = findTreeNode(node.id)
      if (t) onSelect(t)
    },
    [findTreeNode, onSelect],
  )

  const handleEdit = useCallback(
    (node: FlatItem) => {
      const t = findTreeNode(node.id)
      if (t) onEdit(t)
    },
    [findTreeNode, onEdit],
  )

  const handleAddChild = useCallback(
    (node: FlatItem) => {
      const t = findTreeNode(node.id)
      if (t) onAddChild(t)
    },
    [findTreeNode, onAddChild],
  )

  return (
    <Box style={{ position: "relative", minHeight: 60 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visibleIds}
          strategy={verticalListSortingStrategy}
        >
          {flatItems.map((item) => (
            <TreeItem
              key={item.id}
              item={item}
              selectedId={selectedId}
              isExpanded={expanded.has(item.id)}
              onSelect={() => handleSelect(item)}
              onDoubleClick={() => handleEdit(item)}
              onAddChild={() => handleAddChild(item)}
              onToggleExpand={() => toggleExpand(item.id)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  )
}
